import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  IconButton,
  Box,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Tooltip,
  Paper,
} from '@mui/material';

import { BsFillMicFill, BsMicMuteFill } from 'react-icons/bs';
import { MdSearch /* , MdList */ } from 'react-icons/md';
import { useSelector } from 'react-redux';
import {
  opIdiomaCatalogo,
  PLACEHOLDER_SEARCH_TERM_PN_ERP,
} from '../../../../lib/idiomaAppOptions';

function useDebouncedValue(value, delay = 350) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function normalizeReactSelectResults(result) {
  if (!Array.isArray(result)) return [];
  const isGrouped =
    result.length > 0 && Array.isArray(result[0] && result[0].options);
  if (!isGrouped) return result;

  const flat = [];
  for (let i = 0; i < result.length; i += 1) {
    const group = result[i];
    const groupLabel = typeof group.label === 'string' ? group.label : '';
    const arr = Array.isArray(group.options) ? group.options : [];
    for (let j = 0; j < arr.length; j += 1) {
      const opt = arr[j] || {};
      // sem underscores para evitar no-underscore-dangle
      flat.push({ ...opt, groupLabel });
    }
  }
  return flat;
}

export default function SearchAutocomplete({
  idCatalogo,
  getMaterial,
  onOptionSelected,
  onSearchTerm,
  placeholder = 'Digite um termo, partnumber ou código ERP para pesquisar...',
  initialQuery = '',
  disabled = false,
  width,
}) {
  const rootRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const previewImgRef = React.useRef(null);

  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(initialQuery);
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState(null);

  /** idioma do catálogo escolhido pelo usuário (null = padrão) */
  const idiomaCatalogo = useSelector(
    (state) => state.param.idiomaCatalogo || null
  );

  const idioma = idiomaCatalogo || opIdiomaCatalogo.BR;

  const vw = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0
  );
  const PREVIEW_WIDTH = 620;
  const PREVIEW_TOP = 150;
  const PREVIEW_LEFT = 490;
  const PREVIEW_RIGHT = null;
  const [preview, setPreview] = React.useState({
    visible: false,
    src: '',
    alt: '',
    top: 0,
    left: -PREVIEW_WIDTH - 12,
  });

  const [listening, setListening] = React.useState(false);
  const recognitionRef = React.useRef(null);

  const debouncedQuery = useDebouncedValue(inputValue, 350);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        const rec = recognitionRef.current;
        if (rec && typeof rec.stop === 'function') {
          rec.stop();
        }
      } catch (e) {
        /* silencioso */
      }
    };
  }, []);

  const fetchOptions = React.useCallback(
    (q) =>
      new Promise((resolve) => {
        try {
          getMaterial({ id: idCatalogo, value: q }, (res) => {
            resolve(normalizeReactSelectResults(res || []));
          });
        } catch (e) {
          resolve([]);
        }
      }),
    [getMaterial, idCatalogo]
  );

  // Busca remota (debounced)
  React.useEffect(() => {
    const q = typeof debouncedQuery === 'string' ? debouncedQuery.trim() : '';
    if (!q) {
      setOptions([]);
      return undefined;
    }

    let cancel = false;
    setLoading(true);

    fetchOptions(q)
      .then((opts = []) => {
        if (!cancel && mountedRef.current) {
          setOptions(opts);
        }
      })
      .catch(() => {
        if (!cancel && mountedRef.current) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancel && mountedRef.current) {
          setLoading(false);
        }
      });

    return () => {
      cancel = true;
    };
  }, [debouncedQuery, fetchOptions]);

  // const handleShowAll = React.useCallback(() => {
  //   setLoading(true);
  //   fetchOptions('')
  //     .then((opts = []) => {
  //       if (mountedRef.current) {
  //         setOptions(opts);
  //         setOpen(true);
  //       }
  //     })
  //     .catch(() => {
  //       if (mountedRef.current) {
  //         setOptions([]);
  //       }
  //     })
  //     .finally(() => {
  //       if (mountedRef.current) {
  //         setLoading(false);
  //       }
  //     });
  // }, [fetchOptions]);

  const handleSearchTerm = React.useCallback(
    (term) => {
      const t = (term ?? inputValue).trim();
      if (!t) return;
      if (typeof onSearchTerm === 'function') {
        onSearchTerm(t);
      }
      setInputValue(t);
      setOpen(false);
      setPreview((p) => ({ ...p, visible: false }));
    },
    [onSearchTerm, inputValue]
  );

  function sanitizeTerm(term = '') {
    return String(term)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^0-9a-zA-Z\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const toggleMic = React.useCallback(() => {
    const SR =
      typeof window !== 'undefined' &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);

    if (!SR) {
      // eslint-disable-next-line no-alert
      alert('Reconhecimento de voz não é suportado neste navegador.');
      return;
    }

    if (listening) {
      try {
        const rec = recognitionRef.current;
        if (rec && typeof rec.stop === 'function') {
          rec.stop();
        }
      } catch (e) {
        /* silencioso */
      }
      setListening(false);
      return;
    }

    const recognition = new SR();
    recognition.lang = 'pt-BR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (e) => {
      const transcript =
        (e &&
          e.results &&
          e.results[0] &&
          e.results[0][0] &&
          e.results[0][0].transcript) ||
        '';
      const spokenRaw = transcript.trim();

      const spoken = sanitizeTerm(spokenRaw);

      if (spoken) {
        setInputValue(spoken);
        if (typeof onSearchTerm === 'function') {
          onSearchTerm(spoken);
        }
        setOpen(true);
      }
    };

    recognition.onerror = () => {};
    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch (e) {
      setListening(false);
    }
  }, [listening, onSearchTerm]);

  // passou a usar 'groupLabel' (sem underscore)
  const groupBy = (opt) =>
    typeof opt.groupLabel === 'string' ? opt.groupLabel : '';

  const hidePreview = React.useCallback(() => {
    const el = previewRef.current;
    if (el) el.style.display = 'none';
  }, []);

  const showPreviewFor = React.useCallback(
    (opt /* , event */) => {
      const l = opt && opt.label ? opt.label : {};
      if (!l.caminho) {
        hidePreview();
        return;
      }

      const prevEl = previewRef.current;
      const imgEl = previewImgRef.current;
      if (!prevEl || !imgEl) return;

      if (imgEl.getAttribute('src') !== l.caminho) {
        imgEl.setAttribute('src', l.caminho);
      }
      imgEl.setAttribute('alt', l.descricaoPagina || l.descricao || '');

      prevEl.style.display = 'block';
    },
    [hidePreview]
  );

  const handleBlur = React.useCallback(() => {
    setLoading(false);
    hidePreview();
    try {
      const rec = recognitionRef.current;
      if (rec && typeof rec.stop === 'function') {
        rec.stop();
      }
    } catch (e) {
      // silencioso
    }
    setListening(false);
  }, [hidePreview]);

  return (
    <Box ref={rootRef} sx={{ position: 'relative', width }}>
      <Paper
        ref={previewRef}
        elevation={10}
        style={{ display: 'none' }}
        sx={{
          position: 'fixed',
          top: PREVIEW_TOP,
          ...(PREVIEW_LEFT != null ? { left: vw / 2 - PREVIEW_LEFT } : {}),
          ...(PREVIEW_RIGHT != null ? { right: PREVIEW_RIGHT } : {}),
          width: PREVIEW_WIDTH,
          maxHeight: '100vh',
          overflow: 'hidden',
          p: 1,
          border: '5px solid #ccc',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: 1600,
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
        <img
          ref={previewImgRef}
          src=""
          alt="preview image"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            maxHeight: '70vh',
            objectFit: 'contain',
          }}
          draggable={false}
        />
      </Paper>

      <Autocomplete
        disablePortal
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setLoading(false);
          hidePreview();
        }}
        freeSolo
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        disabled={disabled}
        loading={loading}
        options={options}
        value={value}
        groupBy={groupBy}
        filterOptions={(opt) => opt}
        renderGroup={(params) => (
          <li key={params.key}>
            <Box
              sx={{
                color: '#525252',
                borderBottom: '1px solid #cbd5e1',
                px: 2,
                py: 1,
              }}
            >
              {params.group}
            </Box>
            <ul style={{ paddingLeft: 0, margin: 0 }}>{params.children}</ul>
          </li>
        )}
        onChange={(_, newOption) => {
          if (!newOption) {
            setValue(null);
          } else {
            setValue(newOption);
            if (typeof onOptionSelected === 'function') {
              onOptionSelected(newOption);
            }
            setOpen(false);
            hidePreview();
          }
        }}
        inputValue={inputValue}
        onInputChange={(_, newInput, reason) => {
          if (reason === 'input') {
            setInputValue(newInput);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const listbox = document.querySelector('[role="listbox"]');
            const highlighted =
              listbox &&
              listbox.querySelector &&
              listbox.querySelector('[aria-selected="true"]');
            if (!highlighted) {
              e.preventDefault();
              handleSearchTerm(inputValue);
            }
          }
        }}
        getOptionLabel={(opt) => {
          if (typeof opt === 'string') return opt;
          const l = opt && opt.label ? opt.label : {};
          const left = l.partnumber || l.codimate || '';
          const right = l.descricao ? `- ${l.descricao}` : '';
          return `${left} ${right}`.trim();
        }}
        isOptionEqualToValue={(opt, val) =>
          String(opt.value) === String(val && val.value)
        }
        renderOption={(optionProps, opt) => {
          const l = (opt && opt.label) || {};

          const liProps = {
            ...optionProps,
            onMouseEnter: (ev) => {
              if (typeof optionProps.onMouseEnter === 'function')
                optionProps.onMouseEnter(ev);
              showPreviewFor(opt, ev);
            },
            onMouseLeave: (ev) => {
              if (typeof optionProps.onMouseLeave === 'function')
                optionProps.onMouseLeave(ev);
              // hidePreview();
            },
          };

          return (
            <li {...liProps} key={opt.value}>
              <ListItem disableGutters>
                {l.caminho ? (
                  <ListItemAvatar>
                    <Avatar
                      src={l.caminho}
                      alt={l.descricaoPagina}
                      imgProps={{ draggable: false }}
                      sx={{ width: 50, height: 50 }}
                    />
                  </ListItemAvatar>
                ) : null}
                <ListItemText
                  // primary={
                  //   <Box fontWeight={600}>
                  //     {l.partnumber || l.codimate || ''}{' '}
                  //     {l.descricao ? `- ${l.descricao}` : ''}
                  //   </Box>
                  // }
                  primary={
                    <>
                      <Box fontWeight={600}>
                        {l.partnumber || l.codimate || ''}{' '}
                        {l.descricao ? `- ${l.descricao}` : ''}
                      </Box>

                      {l.partnumberPesquisadoEstaDesatualizado && (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            mt: 0.5,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: '#fffbeb',
                            color: '#92400e',
                            border: '1px solid #f59e0b',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          PN pesquisado: {l.partnumberPesquisado} → atual:{' '}
                          {l.partnumberAtual}
                        </Box>
                      )}

                      {!l.partnumberPesquisadoEstaDesatualizado &&
                        l.partnumberAlterado && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              mt: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #93c5fd',
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            Possui histórico de partnumber
                          </Box>
                        )}
                    </>
                  }
                  secondary={
                    <>
                      {l.descricaoPagina}
                      <Box component="span" fontWeight={700}>
                        {' '}
                        |{' '}
                      </Box>
                      {l.fmtPaginaCatalogoNivel}
                    </>
                  }
                />
              </ListItem>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onBlur={handleBlur}
            placeholder={PLACEHOLDER_SEARCH_TERM_PN_ERP[idioma]}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {/* <Tooltip title="Mostrar todos">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        handleShowAll();
                      }}
                      tabIndex={-1}
                    >
                      <MdList size={20} />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip
                    title={listening ? 'Parar microfone' : 'Buscar por voz'}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleMic();
                      }}
                      color={listening ? 'primary' : 'default'}
                      tabIndex={-1}
                    >
                      {listening ? (
                        <BsFillMicFill size={20} />
                      ) : (
                        <BsMicMuteFill size={20} />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Buscar termo">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSearchTerm(inputValue);
                      }}
                      tabIndex={-1}
                    >
                      <MdSearch size={20} />
                    </IconButton>
                  </Tooltip>

                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          style: { maxHeight: 550, overflow: 'auto' },
          onMouseLeave: hidePreview,
          onScroll: hidePreview,
        }}
        PaperComponent={(p) => <Paper {...p} elevation={8} />}
        sx={{ width: '100%' }}
      />
    </Box>
  );
}

SearchAutocomplete.propTypes = {
  idCatalogo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getMaterial: PropTypes.func.isRequired,
  onOptionSelected: PropTypes.func,
  onSearchTerm: PropTypes.func,
  placeholder: PropTypes.string,
  initialQuery: PropTypes.string,
  disabled: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

SearchAutocomplete.defaultProps = {
  idCatalogo: undefined,
  onOptionSelected: undefined,
  onSearchTerm: undefined,
  placeholder: 'Digite um termo, partnumber ou código ERP para pesquisar...',
  initialQuery: '',
  disabled: false,
  width: '100%',
};
